/**
 * Объект вилки так, как его присылает сервер, и подписи для человека.
 *
 * Типы здесь — зеркало `Fork.payload()` из backend/forq/engine/detector.py. Правило
 * совместимости односторонее: сервер поля только ДОБАВЛЯЕТ, поэтому всё, кроме
 * обязательного минимума протокола (`id`, `roi`, `o`, `age`), читается как
 * необязательное. Вкладка сканера живёт у арбитражника неделями, и старый бандл
 * обязан переживать появление новых полей молча, а не падать на разборе.
 *
 * Обратное тоже верно: если поле пропало, значит сервер откатили — показываем
 * прочерк, а не NaN. Число, которого нет, хуже видимого отсутствия числа.
 */

import { nativeMarketLabel } from './nativeMarket';
import { isMap3, isMap4, isSetSport, periodSaid, scopeSaid } from './sports';

export { sportLabel } from './sports';

/**
 * Приговор анти-скам гейта на проводе. `hidden` — подсказка серверного профиля
 * («снял бы»), а не отсутствие строки: студия фильтров считает цену ручки только
 * по тому, что доехало. Жёстко на сервере остаётся `impossible_roi`.
 */
export type ForkStatus = 'clean' | 'flagged' | 'hidden';

/**
 * Имя площадки до оплаты. Сервер подставляет его ВСЕМ ногам сразу (`demo.py`):
 * два разных псевдонима позволяли бы считать по ним покрытие. Значит, это не
 * имя, а его отсутствие, и всякая настройка про площадки обязана считать такую
 * ногу неизвестной. Иначе пара «hidden + hidden» читается как «обе ставки в
 * одной конторе», и настройка, ловящая мнимые вилки, оставляет демо пустой
 * экран — то есть врёт ровно тому, кто ещё выбирает, платить ли.
 */
export const UNNAMED_VENUE = 'hidden';

/**
 * Код площадки, по которому человеку есть что прочесть.
 *
 * Пустая строка — это не контора, а дыра в объекте вилки. Такая «вилка»
 * выглядит как ставка в одну ногу: цифры есть, второй кассы нет. Демо
 * подставляет `hidden` обеим ногам сразу — это маска, не дыра: слово на
 * экране есть, покрытие по нему считать нельзя. Резать `hidden` здесь
 * нельзя: демо осталось бы без ленты.
 */
export const namedSource = (code: string | null | undefined): boolean =>
  (code ?? '').trim() !== '';

export interface ForkRule {
  /** Код правила: stale, odds_anomaly, thin, no_link, … */
  c: string;
  /** Текст с числами внутри — показывается как есть. */
  t: string;
}

export interface ForkLeg {
  /** Код площадки. */
  s: string;
  side: string;
  odds: string;
  url: string | null;
  /** Ведёт ли ссылка на событие. false — только на площадку, матч искать руками. */
  direct?: boolean;
  /**
   * Адрес купона конкретного исхода: не просто страница матча, а форма ставки с
   * уже выбранным исходом. Приходит только когда он точнее `url`, поэтому
   * отсутствие поля — норма, а не ошибка.
   */
  betUrl?: string;
  /**
   * Насколько точен вход: `betslip` — купон исхода, `event` — страница матча,
   * `venue` — только площадка. Лестница рядом с `direct`, потому что `direct`
   * не отличает раздел каталога от страницы события.
   */
  p?: string;
  participants: string[];
  /** Цена шара на прогнозном рынке, если применимо. */
  share: string | null;

  // ── Второй протокол ────────────────────────────────────────────────────────
  /**
   * Чем меряется нога. На бирже покупают ШАРЫ и платят центами за штуку, в
   * конторе ставят ДЕНЬГИ и получают кэф. Это разные действия с разными формами
   * ввода, и строка обязана назвать оба своими словами: иначе оператор вобьёт
   * доллары в поле, которое ждёт количество штук.
   */
  unit?: 'share' | 'money';
  /** Валюта площадки: у международной конторы зависит от счёта, поправимо. */
  cur?: string;
  /** Шаг ставки. У русской конторы десятка: 1001.11 — метка арбитражника. */
  step?: string;
  /** Написание участника НА ЭТОЙ площадке: человек ищет ставку по нему. */
  name?: string | null;
  /**
   * Номер события у площадки. У Positive страницы матча нет — id для Ctrl+F
   * по `data-id` на главной. Старый клиент поле игнорирует.
   */
  eid?: string;
  /**
   * Тройка конторы: `{eventId}~{marketId}~{oddId}`, как `data-label` на кнопке.
   * Пусто у площадки без vendor id. Не путать с `asset` — токен ордера биржи.
   */
  sel?: string;
  /** Период рынка этой ноги: `M` или `P4`. Старый клиент читает его из `fork.market`. */
  period?: string;
  /**
   * Канон ноги со стороной. У зеркала BetBurger на вилке сторона пустая
   * (`HCP:PE:M::2.5:`), а драйверу Polymarket нужен якорь H/A — иначе
   * площадка открывает победу матча.
   */
  market?: string;
  /** Уровни стакана `[цена, объём]`, только у биржевой ноги. */
  book?: [string, string][];
  /** Actual market fee schedule; absent on legacy frames. Rate is a fraction. */
  feeRate?: string;
  feeExponent?: number;
  /** `min` — кривая Predict.fun: feeRate × min(p, 1 − p). Нет поля — кривая Polymarket. */
  feeModel?: 'min';
  /**
   * Токен торгуемого исхода на бирже. Адрес купона говорит человеку, куда идти;
   * ордеру он не говорит ничего — подпись называет исход именно токеном.
   * Приезжает только с биржевой ногой, у которой есть наш стакан.
   */
  asset?: string;
  /** Шаг цены рынка. Потолок ордера обязан быть ему кратен. */
  tick?: string;
  /** Минимальный ордер площадки, в шарах. Меньше — рынок не примет. */
  minSize?: string;
  /**
   * Потолок ноги и его происхождение. Три вида различаются не для точности
   * формулировки: `book` посчитан нами по стакану, `limit` объявлен площадкой,
   * `assumed` не известен никому — его называет оператор. Строка, где потолок
   * допущен, теряет право вести (Правило 1).
   */
  cap?: { kind: 'book' | 'limit' | 'assumed'; value: string | null };
  /**
   * Сколько секунд цена этой ноги стоит без движения. `null` — под наблюдением
   * не была; подменять нулём нельзя, ноль читается как «только что двигалась».
   *
   * То же число приезжает вторым видом — массивом `still` на самой вилке, в
   * порядке ног. Дублирование не наше: массив — часть протокола, снять его
   * нельзя, а экран читает покой с ноги. Обе формы считает движок из одного
   * `motion`, и разъезжаться им запрещено.
   */
  still?: number | null;
  /**
   * Покой ИЗМЕРЕН, а не оценён снизу. Пока цена при нас ни разу не двигалась,
   * отсчёт идёт с момента, как мы ногу увидели, и число значит «стоит не
   * меньше». Порог по покою такую ногу резать не имеет права.
   */
  stillSure?: boolean;
  /**
   * Ставка ПРОТИВ исхода на бирже (lay): человек принимает чужую ставку, как
   * букмекер. Проигрыш такой ноги — сумма × (кэф − 1), и без флага клиент
   * считал её обычной, завышая прибыль в разы (замер 07.08.2026: 185% на
   * экране против честных 36%).
   */
  lay?: boolean;
  /**
   * Этот кэф сейчас в плюсе с наибольшим числом контор (filled) или тоже
   * широкий, но не max этой строки (empty). Нет поля — точки нет. Не порядок
   * ног и не `lead`.
   */
  initiator?: boolean;
  /** Сколько различных контор сейчас в плюсе с этой ценой. Нет поля — цифры нет. */
  initiatorCount?: number | null;
}

/** Что на строке проверено и что проверить было нечем. Плейсхолдеров нет. */
export interface ForkChecks {
  ok: string[];
  unknown: string[];
}

export interface VolumeStep {
  bank: string;
  /** ROI при этом банке, в процентах. */
  roi: string;
}

export interface Fork {
  id: string;
  /** NET-ROI в процентах — главное число ленты. */
  roi?: string;
  /** Кэфы ног в том же порядке, что legs. Порядок стабилен между тактами. */
  o?: string[];
  /** Возраст в секундах на момент отправки. Дальше клиент считает сам. */
  age?: number;
  /** Момент получения на клиенте, мс. Ставится клиентом, сервер его не присылает. */
  at?: number;
  roiGross?: string;
  /** Подтверждённый процент, если сервер его посчитал. Нет ключа — на карточке его нет. */
  roiExecutable?: string | null;
  /**
   * Отпечаток события: дисциплина и состав. Одинаков у всех вилок одного матча.
   *
   * Считает его сервер. Повторить отпечаток на клиенте нельзя: он результат
   * матчинга с нормализацией имён и алиасами, и вторая реализация разошлась бы с
   * первой молча — один матч разъехался бы по двум группам.
   */
  ev?: string;
  sport?: string;
  /** `live` или `prematch`. Может отсутствовать у вкладки, открытой до релиза. */
  line?: string;
  /**
   * Календарный старт матча, ISO. Нет ключа — площадка времени не назвала
   * или подставила отметку котировки. Подпись считает клиент из `ko` и `now`.
   */
  ko?: string;
  league?: string;
  market?: string;
  legs?: ForkLeg[];
  vol?: VolumeStep[];
  maxVol?: string;
  limits?: string[];
  /** Медианная живучесть пары площадок на этом типе рынка, секунды. null — выборки нет. */
  sv?: number | null;
  st?: ForkStatus;
  rl?: ForkRule[];

  // ── Второй протокол ────────────────────────────────────────────────────────
  /** Подпись дисциплины с сервера: иначе новая дисциплина приедет кодом. */
  sportName?: string;
  /**
   * Живучесть по семейству пар и само семейство. Заполнено и тогда, когда `sv`
   * есть: подставлять групповое число вместо своего нельзя — это знание о
   * похожих строках, а не об этой, и разницу обязан назвать тот, кто показывает.
   */
  svGroup?: number | null;
  svFamily?: string;
  checks?: ForkChecks;
  /**
   * Право вести — серверная его половина: все проверки выполнены и живучесть
   * измерена по этой самой паре. Потолок и темп добавляет клиент, и это право
   * он может только отнять.
   */
  lead?: boolean;
  /** Площадка заполненной точки. Нет при ничьей двух filled. */
  initiatorBook?: string;
  /** Свой граф или чужой. Обязателен, если точка есть. */
  initiatorBy?: 'ours' | 'mirror';
  /**
   * Уже публиковали, ROI ушёл ниже пола, ноги живы. Соседям прячем,
   * работе оставляем. Старый бандл поле не знает и просто рисует строку.
   */
  al?: boolean;
  /** ROI выше, чем у `p`% вилок этой пары и спорта за месяц (p95…p99). */
  rare?: { p: number };
  /**
   * Строка-дубль: та же вилка, но нога открывается у другой вывески одной
   * линии. Значение — код вывески-донора, чей кэф показан. Ставить по ней
   * можно только сверив цену: у вывесок одной линии цены расходятся.
   */
  dub?: string;
  /**
   * Последний поиск этой вилки не содержал. Кэф и процент — прежние, строка
   * досиживает несколько секунд. Нет ключа — цифры из последнего ответа.
   */
  lag?: boolean;
  [key: string]: unknown;
}

/**
 * Полувилка: две ставки, которые могут выиграть ОБЕ. Это НЕ вилка.
 *
 * Отдельный тип, а не флаг на `Fork`, потому что это другой товар. У вилки
 * прибыль гарантирована и описывается одним числом; у полувилки гарантии нет, и
 * описывается она двумя: сколько стоит промах и сколько приносит попадание.
 * Свести их к одному проценту — значит обещать гарантию там, где её нет.
 */
export interface Middle {
  id: string;
  /** Сколько теряется от банка при промахе, в процентах. Отрицательное — не теряется. */
  cost?: string;
  /** Сколько приносит попадание, в процентах от банка. */
  payout?: string;
  /** Сколько целых исходов помещается в окно: 2.5/3.5 — один, 2.5/4.5 — два. */
  outcomes?: number;
  /** Ставки на ноги при банке 100. Раскладку человек обязан видеть, а не считать. */
  stakes?: string[];
  o?: string[];
  age?: number;
  at?: number;
  sport?: string;
  league?: string;
  market?: string;
  legs?: ForkLeg[];
  st?: ForkStatus;
  rl?: ForkRule[];
  [key: string]: unknown;
}

const MARKETS: Record<string, string> = {
  WIN: 'победа',
  TTL: 'тотал',
  HCP: 'фора',
  ITTL: 'инд. тотал',
  PROP: 'проп',
  RACE: 'гонка',
  NTH: 'объект',
};

const SIDES: Record<string, string> = {
  H: '1',
  A: '2',
  D: 'X',
  O: 'больше',
  U: 'меньше',
  Y: 'да',
  N: 'нет',
};

/**
 * Их сырая строка канала A, не наш ключ из шести полей.
 * Переводы — проверенная таблица исходов из базы знаний, не догадка.
 * Нет строки в таблице — оставляем ключ как есть.
 */
const THEIR_TITLE: Record<string, string> = {
  'PROP:KK:P': 'убийство ножом',
  'PROP:HEK:P': 'убийство осколочной',
  'PROP:MCK:P': 'убийство молотовым',
  'PROP:MGC:P': 'будут мегакрипы',
  'PROP:GDL:P': 'будет godlike',
  'PROP:BGL:P': 'будет beyond godlike',
  'RACE:PO:P': 'гонка убийств',
  'RACE:SP:P': 'гонка раундов',
  'NTH:TW:P': 'башня',
  'NTH:BR:P': 'барак',
  'NTH:RN:P': 'рошан',
  'TTL:DU:P': 'тотал времени',
  'TTL:TW:P': 'тотал башен',
  'TTL:BR:P': 'тотал бараков',
  'TTL:RN:P': 'тотал рошанов',
};

/**
 * Зеркало Кибер ГГ: `WIN::PS:карта:раунд:сторона`.
 * Наш шестипольный разбор читает раунд как линию и пишет «победа» — человек
 * открывает Match Winner (engl 06.10 Legacy–M80: стол 2.05, касса 1.65).
 */
export function roundWinOf(
  key: string | undefined,
): { map: number; round: number; side: string } | null {
  if (!key) return null;
  const parts = key.split(':');
  if (parts.length !== 6) return null;
  const [kind, scope, period, mapRaw, roundRaw, side] = parts;
  if (kind !== 'WIN' || scope !== '' || period !== 'PS') return null;
  if (!/^[1-9]\d*$/.test(mapRaw ?? '') || !/^[1-9]\d*$/.test(roundRaw ?? '')) return null;
  if (!/^(H|A|1|2)$/.test(side ?? '')) return null;
  return { map: Number(mapRaw), round: Number(roundRaw), side: side! };
}

/** «раунд 12 · карта 1» — не «победа». Сетным — лег и сет. */
export function roundWinSaid(key: string | undefined, sport?: string): string | null {
  const got = roundWinOf(key);
  if (got === null) return null;
  if (isSetSport(sport)) return `лег ${got.round} · сет ${got.map}`;
  return `раунд ${got.round} · карта ${got.map}`;
}

type TheirMarket = {
  kind: string;
  scope: string;
  periodKind: string;
  period: string;
  side: string;
  line: string;
};

function parseTheir(key: string): TheirMarket | null {
  const parts = key.split(':');
  if (parts.length < 4) return null;
  const kind = parts[0] ?? '';
  const scope = parts[1] ?? '';
  const periodKind = parts[2] ?? '';
  let rest = parts.slice(3);
  let period = '';
  if (periodKind === 'M') {
    period = 'M';
  } else if (periodKind === 'P' && rest[0] !== undefined && /^\d+$/.test(rest[0])) {
    period = `P${rest[0]}`;
    rest = rest.slice(1);
  } else {
    return null;
  }
  const exotic =
    kind === 'PROP' ||
    kind === 'RACE' ||
    kind === 'NTH' ||
    (kind === 'TTL' && (scope === 'DU' || scope === 'TW' || scope === 'BR' || scope === 'RN'));
  if (!exotic) return null;
  let side = rest[0] ?? '';
  let line = rest[1] ?? '';
  if (kind === 'PROP') {
    line = '';
  }
  return { kind, scope, periodKind, period, side, line };
}

function theirWhat(got: TheirMarket, sport?: string): string | null {
  const code = `${got.kind}:${got.scope}:${got.periodKind}`;
  let title = THEIR_TITLE[code];
  if (title === undefined) return null;
  if (code === 'RACE:SP:P' && (sport === 'darts' || sport === 'table_tennis')) {
    title = 'гонка легов';
  }
  const bits: string[] = [];
  if (got.kind === 'RACE' && got.line !== '') {
    bits.push(`гонка до ${got.line}`, title.replace(/^гонка\s+/, ''));
  } else if (got.kind === 'NTH' && got.line !== '') {
    bits.push(`${got.line}-я`, title);
  } else if (got.kind === 'TTL' && got.line !== '') {
    bits.push(title, got.line);
  } else {
    bits.push(title);
  }
  if (got.period !== 'M') {
    const said = periodSaid(got.period, sport);
    if (said !== '') bits.push(said);
  }
  return bits.join(' ');
}

/**
 * Ключ рынка → подпись шапки вилки. Формат: market:scope:period:side:line:subject.
 *
 * Стороны в ключе нет для шапки: ключ вилки несёт сторону первой ноги, и
 * «тотал 2.5 больше» над двумя плитками читалось как две ставки на одно
 * (24.08, Z5J2 / D3H6). Куда ставить — `betOn` по `leg.side`.
 *
 * Неизвестный ключ возвращается как есть: сервер может завести рынок, о котором
 * этот бандл не знает, и показать сырой ключ честнее, чем подписать наугад.
 */
export function marketLabel(key: string | undefined, sport?: string): string {
  if (!key) return '—';
  const native = nativeMarketLabel(key);
  if (native !== null) return native;
  const round = roundWinSaid(key, sport);
  if (round !== null) return round;
  const theirs = parseTheir(key);
  if (theirs !== null) {
    return theirWhat(theirs, sport) ?? key;
  }
  const parts = key.split(':');
  if (parts.length !== 6) return key;
  const [market, scope, period, , line] = parts as [
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  const base = MARKETS[market];
  if (!base || market === 'PROP' || market === 'RACE' || market === 'NTH') return key;
  const bits = [base];
  if (line) bits.push(line);
  if (period !== 'M') {
    const said = periodSaid(period, sport);
    if (said !== '') bits.push(said);
  }
  const unit = scopeSaid(scope, sport);
  if (unit) bits.push(unit);
  return bits.join(' ');
}

/**
 * Ключ рынка, разобранный на «что это» и «куда ставит эта нога».
 *
 * Целой подписи (`marketLabel`) хватает вилке: её ноги стоят на двух сторонах
 * ОДНОЙ линии, и рынок называется один раз на строку. Коридор тем и коридор,
 * что линии разные — «меньше 143.5» против «больше 138.5», — и подпись у него
 * разъезжается на две части: общее «тотал по очкам» стоит у матча, а сторона с
 * числом принадлежит конкретной ноге и обязана стоять рядом с её суммой.
 *
 * Число линии отдаётся отдельно от подписи: по паре чисел страница показывает
 * само окно («между 138.5 и 143.5»), а из склеенной строки его пришлось бы
 * выковыривать разбором обратно.
 */
export interface MarketParts {
  /** «тотал по очкам карта 1» — что это за рынок. Чужой ключ возвращается как есть. */
  what: string;
  /** «меньше 143.5» — куда ставит эта нога. Пусто, если стороны в ключе нет. */
  pick: string;
  /** Линия числом. `null` — линии у рынка нет (победа) или она не число. */
  line: number | null;
}

export function marketParts(key: string | undefined): MarketParts {
  const round = roundWinOf(key);
  if (round !== null) {
    return {
      what: roundWinSaid(key) ?? '—',
      pick: SIDES[round.side] ?? round.side,
      line: null,
    };
  }
  const theirs = parseTheir(key ?? '');
  if (theirs !== null) {
    const what = theirWhat(theirs) ?? key ?? '—';
    const value = Number.parseFloat(theirs.line);
    return {
      what,
      pick: SIDES[theirs.side] ?? theirs.side,
      line: Number.isFinite(value) ? value : null,
    };
  }
  const parts = (key ?? '').split(':');
  if (parts.length !== 6) return { what: key ?? '—', pick: '', line: null };
  const [market, scope, period, side, line] = parts as [
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  const bits = [MARKETS[market] ?? market];
  if (period !== 'M') {
    const said = periodSaid(period);
    if (said !== '') bits.push(said);
  }
  const unit = scopeSaid(scope);
  if (unit) bits.push(unit);
  const value = Number.parseFloat(line);
  return {
    what: bits.join(' '),
    pick: [SIDES[side] ?? side, line].filter((one) => one !== '').join(' '),
    line: Number.isFinite(value) ? value : null,
  };
}

/**
 * Код рынка из ключа: WIN, TTL, HCP, ITTL. Пустая строка — ключа нет или он чужой.
 *
 * Только НАШЕ написание, без фраз донора, и потому это не «семейство»: фильтр
 * старого экрана строит чипы прямо по кодам ключа. Человеческое семейство —
 * `marketFamily` ниже; раньше обе функции назывались одинаково и жили в разных
 * файлах, и какая из них имелась в виду, приходилось выяснять по импорту.
 */
export const marketPrefix = (key: string | undefined): string => (key ?? '').split(':')[0] ?? '';

/** Подпись кода рынка для чипа фильтра. Незнакомое печатается как есть. */
export const familyLabel = (family: string): string => MARKETS[family] ?? family;


/**
 * «На что ставим» одним словом: победа, тотал, фора, двойной шанс, прочее.
 *
 * Замер 07.08.2026 на живой ленте (290 строк): четыре семейства покрывают 98%,
 * при 81 разном написании рынка. Вилочник, работающий только тоталы, отсекает
 * две трети ленты одним нажатием — ради этого семейство и заведено.
 */
export type MarketFamily = 'win' | 'total' | 'handicap' | 'double' | 'map3' | 'map4' | 'other';

export const FAMILY_NAMES: Record<MarketFamily, string> = {
  win: 'победа',
  total: 'тотал',
  handicap: 'фора',
  double: 'двойной шанс',
  map3: 'исход 3 карты',
  map4: 'исход 4 карты',
  other: 'прочее',
};

export function marketFamily(fork: Fork): MarketFamily {
  const raw = typeof fork.market === 'string' ? fork.market : '';
  // Четвёртая карта раньше жила внутри «победы» и «форы». Антон 08.09: их
  // хотят обходить отдельно — фишка снимает их, не снимая матч.
  if (isMap4(raw, fork.sport)) return 'map4';
  if (isMap3(raw, fork.sport)) return 'map3';
  const m = raw.toLowerCase();
  // Порядок проверок держит смесь двух написаний: наш код («TTL:PE:M:O:2.5:»)
  // и фраза донора («2-way: Гандикап/Фора команда 1 0»). Фраза форы содержит
  // «2-way», поэтому фора проверяется раньше победы; «прочее» — честное имя
  // для экзотики, а не мусорное ведро: её в ленте единицы строк.
  if (m.startsWith('ttl') || m.startsWith('ittl') || m.includes('тотал') || m.includes('total') ||
      m.includes('больше') || m.includes('меньше') || m.includes(' over ') || m.includes(' under ') ||
      /^(over|under)\s/.test(m) || /\s(over|under)$/.test(m)) {
    return 'total';
  }
  if (m.startsWith('hcp') || m.includes('гандикап') || m.includes('фора') || m.includes('handicap')) {
    return 'handicap';
  }
  if (m.includes('двойной шанс') || m.includes('double chance') || m.includes('ничья или') ||
      m.includes('ничья /') || m.includes('/ 1x') || m.includes('x2') || m.includes('dc:')) {
    return 'double';
  }
  if (
    m.startsWith('win') ||
    m.includes('победит') ||
    m.includes('победа') ||
    m.includes('moneyline') ||
    m.includes('draw no bet') ||
    m.includes('3-way') ||
    m.includes('2-way')
  ) {
    return 'win';
  }
  return 'other';
}

export const sideLabel = (side: string): string => SIDES[side] ?? side;

/**
 * Возраст вилки в секундах на текущий момент.
 *
 * Сервер присылает возраст один раз и не обновляет его каждую секунду: дельта ради
 * тикающего счётчика — это десятки килобайт в секунду на клиента (ровно то, чем
 * грешит сканер конкурента). Поэтому дальше время идёт локально от момента получения.
 */
export function ageOf(fork: Fork | Middle, now: number): number {
  const base = fork.age ?? 0;
  const since = fork.at === undefined ? 0 : Math.max(0, Math.floor((now - fork.at) / 1000));
  return base + since;
}

/**
 * ROI в проценты числом. Нечитаемое значение — −∞, а не NaN.
 *
 * Следствие намеренное: такая вилка не проходит ни один порог фильтра и в ленту не
 * попадает. Строка с прочерком на месте главного числа — приглашение поставить
 * наугад, а NaN ещё и ломает сортировку всей таблицы.
 */
export function roiValue(fork: Fork): number {
  const parsed = Number.parseFloat(fork.roi ?? '');
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

export const sourcesOf = (fork: Fork): string[] => (fork.legs ?? []).map((leg) => leg.s);

/**
 * Подпись входа на ногу. Ссылка на главную страницу площадки — не вход на событие,
 * и делать вид, что это одно и то же, нельзя: человек жмёт и попадает не туда, пока
 * вилка живёт секунды.
 */
export function entryHint(leg: ForkLeg | undefined): string {
  if (!entryUrl(leg)) return 'ссылки нет, искать вручную';
  if (leg?.betUrl) return `купон исхода: ${leg.betUrl}`;
  return leg?.direct === false ? 'откроется площадка, матч искать вручную' : (leg?.url ?? '');
}

/**
 * Куда вести человека по нажатию. Купон исхода главнее страницы события: вилка
 * живёт секунды, и каждый лишний клик на стороне конторы — это её половина.
 *
 * Поле необязательное намеренно. Купон ссылкой умеет не всякая площадка, и
 * подставлять вместо него угаданный адрес нельзя: страница-ошибка стоит дороже
 * честной страницы матча.
 */
export function entryUrl(leg: ForkLeg | undefined): string | null {
  return leg?.betUrl ?? leg?.url ?? null;
}
